// Per-row interactive cell for the Live In Club page. Subscribes to a
// page-level xstate actor (one per talent_id, kept in TalentRowStore) so
// the row's machine state + active toast handle survive the section swap
// that happens when a toggle moves the row between in_club / not_in_club.
//
// Same-row re-tap during the undo window routes through Undo because the
// row's snapshot is the authoritative source for the row's current state.
// Cross-row coordination (one toast at a time) lives in NotificationProvider
// — supplant fires the prior toast's on_dismiss, which routes back to the
// hoisted actor via the SUPPLANT event.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from '@xstate/react';
import { useEffect, useRef } from 'react';

import { classlist } from '~/_shared/design-system/lib/classlist';
import { Avatar } from '~/_shared/design-system/ui/atoms/avatar/avatar';
import { PrimaryButton } from '~/_shared/design-system/ui/atoms/button/primary/primary-button';
import { TextButton } from '~/_shared/design-system/ui/atoms/button/text/text-button';
import { Typography } from '~/_shared/design-system/ui/atoms/typography/typography';
import { useToast } from '~/_shared/design-system/ui/molecules/toast/notification-provider';
import type { ClientObservabilityLike } from '~/_shared/observability/react/use-client-observability';
import { useClientObservability } from '~/_shared/observability/react/use-client-observability';

import { toggleTalentStatusServerFn } from '../../application/toggle-talent-status.fn';
import type { StatusValue } from '../../domain/status';
import type { ListLiveInClubResult, LiveInClubEntry } from '../../domain/types';
import {
	applyToggleToCache,
	liveInClubQueryKey,
	resolveRowTimestamp,
} from './query-key';
import { useTalentRowStore } from './talent-row-store';

interface TalentRowItemProps {
	entry: LiveInClubEntry;
	is_mounted: boolean;
	club_id: string;
}

const TalentRowItem = ({ entry, is_mounted, club_id }: TalentRowItemProps) => {
	const { toast } = useToast();
	const queryClient = useQueryClient();
	const observability = useClientObservability();
	const { getActor, toastHandles } = useTalentRowStore();

	const actor = getActor(entry.id);
	// `useSelector` subscribes to the hoisted actor's snapshot (re-renders
	// when state changes); `actor.send` is stable, so we don't memo it.
	const snapshot = useSelector(actor, (s) => s);
	const send = actor.send;

	const toggle_mutation = useMutation({
		mutationFn: ({
			talentId,
			status,
			restore_last_in_club_at,
		}: {
			talentId: string;
			status: StatusValue;
			restore_last_in_club_at?: string | null;
		}) =>
			toggleTalentStatusServerFn({
				data: { talentId, status, restore_last_in_club_at },
			}),
	});

	// Latest-callback refs let the action button's onClick stay stable even
	// when handle_tap / handle_undo close over freshly-rendered state.
	// The placeholder arrows below are overwritten by the useEffect below
	// before any onClick can fire; v8 reports them as uncovered functions
	// because they're never invoked at runtime.
	const handlers = useRef({
		/* v8 ignore start */
		tap: async () => {},
		undo: async () => {},
		/* v8 ignore stop */
	});

	const state = snapshot.value;
	const is_loading = state === 'submitting' || state === 'reverting';
	const button_label = entry.status === 'in_club' ? 'Clock out' : 'Clock in';

	const log = (
		obs: ClientObservabilityLike | null,
		message: string,
		attrs: Record<string, unknown>,
	) => {
		obs?.log({ level: 'info', message, attrs });
	};

	const handle_tap = async () => {
		if (snapshot.value === 'undo_window') {
			await handlers.current.undo();
			return;
		}
		const target_status: StatusValue =
			entry.status === 'in_club' ? 'not_in_club' : 'in_club';

		log(observability, 'live-in-club.tap', {
			talent_id: entry.id,
			target_status,
		});
		// Capture the row's pre-toggle lastInClubAt so the undo path can
		// restore it (G1). Stored in the actor's context for survival.
		send({
			type: 'TAP',
			target_status,
			prior_last_in_club_at: entry.lastInClubAt,
		});

		try {
			const newRow = await toggle_mutation.mutateAsync({
				talentId: entry.id,
				status: target_status,
			});
			log(observability, 'live-in-club.submit_success', {
				talent_id: entry.id,
				target_status,
			});
			send({ type: 'SUBMIT_SUCCESS' });
			// Synchronously update the cache so the row jumps sections
			// immediately. Row state survives the swap because the actor
			// + toast handle live in TalentRowStore, not in this component.
			/* v8 ignore next — `newRow` is null only on session expiry; the
			   route guard redirects on the next render. */
			if (newRow) {
				queryClient.setQueryData(
					liveInClubQueryKey(club_id),
					(prev: ListLiveInClubResult | null | undefined) =>
						applyToggleToCache(prev, entry, newRow),
				);
			}

			const handle = toast({
				variant: 'success',
				title:
					target_status === 'in_club'
						? `${entry.stageName} is at the club`
						: `${entry.stageName} left the club`,
				action: (
					<TextButton
						id={`${entry.id}-toast-undo`}
						type='button'
						onClick={() => handlers.current.undo()}
					>
						Undo
					</TextButton>
				),
				on_dismiss(reason) {
					if (reason === 'supplant') send({ type: 'SUPPLANT' });
					/* v8 ignore start — timer/esc paths are async + Radix-driven;
					   exercised in Playwright e2e, not the jsdom unit. */ else if (
						reason === 'timeout' ||
						reason === 'esc'
					)
						send({ type: 'WINDOW_ELAPSED' });
					/* v8 ignore stop */
				},
			});
			toastHandles.set(entry.id, handle);
		} catch (error) {
			observability?.reportError(error, {
				talent_id: entry.id,
				target_status,
			});
			send({ type: 'SUBMIT_FAILURE', error: error as Error });
			const handle = toast({
				variant: 'error',
				title: `Couldn't update ${entry.stageName}'s status`,
				action: (
					<TextButton
						id={`${entry.id}-toast-retry`}
						type='button'
						onClick={() => handlers.current.tap()}
					>
						Retry
					</TextButton>
				),
				/* v8 ignore start — on_dismiss reasons are async + Radix-driven;
				   exercised in Playwright e2e, not the jsdom unit. */
				on_dismiss(reason) {
					if (reason === 'supplant') send({ type: 'SUPPLANT' });
					else if (reason === 'timeout' || reason === 'esc')
						send({ type: 'ERROR_TIMEOUT' });
				},
				/* v8 ignore stop */
			});
			toastHandles.set(entry.id, handle);
		}
	};

	const handle_undo = async () => {
		const target_status = snapshot.context.target_status;
		/* v8 ignore next — only callable from undo_window, target_status is always set */
		if (!target_status) return;
		const inverse: StatusValue =
			target_status === 'in_club' ? 'not_in_club' : 'in_club';
		// Read the pre-toggle lastInClubAt from the actor's context (G1).
		// Survives row remount because the actor lives in TalentRowStore.
		const restore = snapshot.context.prior_last_in_club_at;

		log(observability, 'live-in-club.undo_click', { talent_id: entry.id });
		send({ type: 'UNDO_CLICK' });

		// Keep the toast visible with a loading-state Undo button while the
		// inverse mutation runs. Dismiss after the revert settles. May be
		// supplanted by a sibling row before this point — in that case
		// update() is a no-op (the Provider's id guard).
		const activeHandle = toastHandles.get(entry.id);
		activeHandle?.update({
			action: (
				<TextButton
					id={`${entry.id}-toast-undo`}
					type='button'
					is_loading
					disabled
				>
					Undo
				</TextButton>
			),
		});

		try {
			const newRow = await toggle_mutation.mutateAsync({
				talentId: entry.id,
				status: inverse,
				restore_last_in_club_at: restore,
			});
			log(observability, 'live-in-club.undo_success', { talent_id: entry.id });
			send({ type: 'REVERT_SUCCESS' });
			/* v8 ignore next — same defensive null-on-session-expiry guard. */
			if (newRow) {
				queryClient.setQueryData(
					liveInClubQueryKey(club_id),
					(prev: ListLiveInClubResult | null | undefined) =>
						applyToggleToCache(prev, entry, newRow),
				);
			}
			activeHandle?.dismiss();
			toastHandles.delete(entry.id);
		} catch (error) {
			observability?.reportError(error, {
				talent_id: entry.id,
				context: 'revert',
			});
			send({ type: 'REVERT_FAILURE', error: error as Error });
			activeHandle?.dismiss();
			toastHandles.delete(entry.id);
			toast({
				variant: 'error',
				title: `Couldn't undo ${entry.stageName}'s status change`,
			});
		}
	};

	// Keep the latest closures available to the action ref so the
	// rendered button's onClick can call into the freshest snapshot.
	useEffect(() => {
		handlers.current.tap = handle_tap;
		handlers.current.undo = handle_undo;
	});

	const timestamp = resolveRowTimestamp(entry, is_mounted);

	return (
		<li className='live-in-club-page__row'>
			<Avatar display_name={entry.stageName} size='sm' />
			<div className='live-in-club-page__row-text'>
				<Typography variant='body' className='live-in-club-page__row-name'>
					{entry.stageName}
				</Typography>
				{timestamp ? (
					<Typography
						variant='footer'
						className='live-in-club-page__row-timestamp'
					>
						{timestamp}
					</Typography>
				) : null}
			</div>
			<div className='live-in-club-page__row-action'>
				<PrimaryButton
					id={`${entry.id}-toggle`}
					size='sm'
					is_loading={is_loading}
					onClick={() => handlers.current.tap()}
					className={classlist(
						entry.status === 'in_club' &&
							'live-in-club-page__row-action-button--out',
					)}
				>
					{button_label}
				</PrimaryButton>
			</div>
		</li>
	);
};

export { TalentRowItem };
